import * as React from 'react';
import { Action } from 'redux';
import { Dispatch } from 'react-redux';
import { authorizationService } from '@services/AuthorizationService';
import { ContractCviCostHistoryViewModel } from '@models/ContractCviCostHistoryViewModel';
import { contractService } from '@services/ContractService';
import { SwoCviViewModel } from '@models/AuthorizationSwoCvi';
import { AuthReportSummaryViewModel, AuthReportFormViewModel } from '@models/AuthReportSummary';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { AppState } from '..';
import { requestAction } from '@common/components/common.actions';
import { AuthorizationChargeBreakdown } from '@models/AuthorizationChargeBreakdown';
// import { PurchaseRequestViewModel } from '@models/PurchaseRequest';
import { swoService } from '../_services/ServiceWorkOrderService';
import { ContractTotalCostHistoryViewModel } from '@models/ServiceWorkOrder';
import { PurchaseRequestSimpleViewModel } from '@models/PurchaseRequest';
import { AppDispatcher } from '@models/common.models';

export enum AuthActionType {
  GetCvisStart = 'GetCvisStart',
  GetCvisSuccess = 'GetCvisSuccess',
  GetCvisFailure = 'GetCvisFailure',
  GetAuthReportSummaryStart = 'GetAuthReportSummaryStart',
  GetAuthReportSummarySuccess = 'GetAuthReportSummarySuccess',
  GetAuthReportSummaryFailure = 'GetAuthReportSummaryFailure',
  GetContractCviCostHistoryStart = 'GetContractCviCostHistoryStart',
  GetContractCviCostHistorySuccess = 'GetContractCviCostHistorySuccess',
  GetContractCviCostHistoryFailure = 'GetContractCviCostHistoryFailure',
  SetSelectedCvi = 'SetSelectedCvi',

  SetReportId = 'SetReportId',

  SaveAuthoDiscount = 'SaveAuthoDiscount',
  SaveAuthorizationFormProgress = 'SaveAuthorizationFormProgress',
  // CreatePurchaseRequestStart = 'CreatePurchaseRequestStart',
  // CreatePurchaseRequestSuccess = 'CreatePurchaseRequestSuccess',
  // CreatePurchaseRequestFailure = 'CreatePurchaseRequestFailure',

  SetChargeBreakdowns = 'SetChargeBreakdowns',

  /***** Contract Cost History *****/
  GetContractCostHistoryStart = 'GetAuthContractCostHistoryStart',
  GetContractCostHistorySuccess = 'GetAuthContractCostHistorySuccess',
  GetContractCostHistoryFailure = 'GetAuthContractCostHistoryFailure',

  GetAuthoHistoryStart = 'GetAuthoHistoryStart',
  GetAuthoHistorySuccess = 'GetAuthoHistorySuccess',
  GetAuthoHistoryFailure = 'GetAuthoHistoryFailure',

  SetPurchaseRequests = 'SetPurchaseRequests'
}

export interface SetReportIdAction extends Action {
  readonly type: AuthActionType.SetReportId;
  readonly id: number;
}

export interface SetChargeBreakdownsAction extends Action {
  readonly type: AuthActionType.SetChargeBreakdowns;
  readonly breakdowns: Array<AuthorizationChargeBreakdown>;
}

export interface SaveAuthoDiscountAction extends Action {
  readonly type: AuthActionType.SaveAuthoDiscount;
  readonly discount: number;
}

export interface SaveAuthorizationFormProgressAction extends Action {
  readonly type: AuthActionType.SaveAuthorizationFormProgress;
  readonly response: AuthReportFormViewModel;
}

export interface GetCvisStartAction extends Action {
  readonly type: AuthActionType.GetCvisStart;
}

export interface GetCvisSuccessAction extends Action {
  readonly type: AuthActionType.GetCvisSuccess;
  readonly cvis: Array<SwoCviViewModel>;
  readonly currentCvi: SwoCviViewModel;
}

export interface GetCvisFailureAction extends Action {
  readonly type: AuthActionType.GetCvisFailure;
  readonly errorMessage: string;
}

export interface GetAuthReportSummaryStart extends Action {
  readonly type: AuthActionType.GetAuthReportSummaryStart;
}

export interface GetAuthReportSummarySuccess extends Action {
  readonly type: AuthActionType.GetAuthReportSummarySuccess;
  readonly authReportSummary: AuthReportSummaryViewModel;
}

export interface GetAuthReportSummaryFailure extends Action {
  readonly type: AuthActionType.GetAuthReportSummaryFailure;
  readonly errorMessage: string;
}

export interface GetContractCviCostHistoryStartAction extends Action {
  readonly type: AuthActionType.GetContractCviCostHistoryStart;
}

export interface GetContractCviCostHistorySuccessAction extends Action {
  readonly type: AuthActionType.GetContractCviCostHistorySuccess;
  readonly contractCviCostHistory: ContractCviCostHistoryViewModel;
}

export interface GetContractCviCostHistoryFailureAction extends Action {
  readonly type: AuthActionType.GetContractCviCostHistoryFailure;
  readonly errorMessage: string;
}

export interface SetSelectedCviAction extends Action {
  readonly type: AuthActionType.SetSelectedCvi;
  readonly selectedCvi: SwoCviViewModel;
}

// export interface CreatePurchaseRequestComponentStartAction extends Action {
//   readonly type: AuthActionType.CreatePurchaseRequestStart;
// }

// export interface CreatePurchaseRequestComponentSuccessAction extends Action {
//   readonly type: AuthActionType.CreatePurchaseRequestSuccess;
// }

// export interface CreatePurchaseRequestComponentFailureAction extends Action {
//   readonly type: AuthActionType.CreatePurchaseRequestFailure;
//   readonly errorMessage: string;
// }

/******* Contract Cost History *******/
export interface GetContractCostHistoryStartAction extends Action {
  readonly type: AuthActionType.GetContractCostHistoryStart;
}
export interface GetContractCostHistorySuccessAction extends Action {
  readonly type: AuthActionType.GetContractCostHistorySuccess;
  readonly costHistory: Array<ContractTotalCostHistoryViewModel>;
}
export interface GetContractCostHistoryFailureAction extends Action {
  readonly type: AuthActionType.GetContractCostHistoryFailure;
  readonly message: string;
}

export interface GetAuthoHistoryStartAction extends Action {
  readonly type: AuthActionType.GetAuthoHistoryStart;
}
export interface GetAuthoHistorySuccessAction extends Action {
  readonly type: AuthActionType.GetAuthoHistorySuccess;
  readonly buildingPermitHistory: number;
  readonly codeUpgradeHistory: number;
}
export interface GetAuthoHistoryFailureAction extends Action {
  readonly type: AuthActionType.GetAuthoHistoryFailure;
  readonly message: string;
}

export function getCvis(swoId: number, swoDetail?: number) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(getCvisStart());
    try {
      let cvis: Array<SwoCviViewModel> = await swoService.getSwoCvis(swoId, false);
      return dispatch(getCvisSuccess(cvis, swoDetail));
    } catch (e) {
      return dispatch(getCvisFailure(e.toString()));
    }
  };
}

export function getCvisStart(): GetCvisStartAction {
  return {
    type: AuthActionType.GetCvisStart
  };
}

export function getCvisSuccess(cvis: Array<SwoCviViewModel>, swoDetail?: number): GetCvisSuccessAction {
  let currentCvi = cvis[0];
  if (swoDetail) {
    currentCvi = cvis.find(cvi => cvi.swoDetailNumber === +swoDetail)!;
  }
  return {
    type: AuthActionType.GetCvisSuccess,
    cvis: cvis,
    currentCvi: currentCvi
  };
}

export function getCvisFailure(e: string): GetCvisFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={e} />, ToastOptions.Error);

  return {
    type: AuthActionType.GetCvisFailure,
    errorMessage: e
  };
}

export function getAuthReportSummary(swoId: number) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(getAuthReportSummaryStart());
    try {
      let authReport: AuthReportSummaryViewModel = await authorizationService.getAuthReportSummary(swoId);
      return dispatch(getAuthReportSummarySuccess(authReport));
    } catch (e) {
      return dispatch(getAuthReportSummaryFailure(e.toString()));
    }
  };
}

export function getAuthReportSummaryStart(): GetAuthReportSummaryStart {
  return {
    type: AuthActionType.GetAuthReportSummaryStart
  };
}

export function getAuthReportSummarySuccess(
  authReportSummary: AuthReportSummaryViewModel
): GetAuthReportSummarySuccess {
  return {
    type: AuthActionType.GetAuthReportSummarySuccess,
    authReportSummary: authReportSummary
  };
}

export function getAuthReportSummaryFailure(e: string): GetAuthReportSummaryFailure {
  return {
    type: AuthActionType.GetAuthReportSummaryFailure,
    errorMessage: e
  };
}

export function getContractCviCostHistory(contractCviId: number) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(GetContractCviCostHistoryStart());
    try {
      const contractCviCostHistory: ContractCviCostHistoryViewModel = await contractService.getContractCviCostHistory(
        contractCviId
      );
      return dispatch(GetContractCviCostHistorySuccess(contractCviCostHistory));
    } catch (e) {
      return dispatch(GetContractCviCostHistoryFailure(e.toString()));
    }
  };
}

export function GetContractCviCostHistoryStart(): GetContractCviCostHistoryStartAction {
  return {
    type: AuthActionType.GetContractCviCostHistoryStart
  };
}

export function GetContractCviCostHistorySuccess(
  contractCviCostHistory: ContractCviCostHistoryViewModel
): GetContractCviCostHistorySuccessAction {
  return {
    type: AuthActionType.GetContractCviCostHistorySuccess,
    contractCviCostHistory: contractCviCostHistory
  };
}

export function GetContractCviCostHistoryFailure(e: string): GetContractCviCostHistoryFailureAction {
  return {
    type: AuthActionType.GetContractCviCostHistoryFailure,
    errorMessage: e
  };
}

export function SetSelectedCvi(selectedCvi: SwoCviViewModel): SetSelectedCviAction {
  return {
    type: AuthActionType.SetSelectedCvi,
    selectedCvi: selectedCvi
  };
}

// export function createPurchaseRequest(swoId: number, purchaseInfo: PurchaseRequestViewModel) {
//   return async (dispatch: Dispatch<any>) => {
//     dispatch(CreatePurchaseRequestComponentStart());
//     try {
//       await authorizationService.createPurchaseRequest(swoId, purchaseInfo);
//       return dispatch(CreatePurchaseRequestComponentSuccess());
//     } catch (e) {
//       return dispatch(CreatePurchaseRequestComponentFailure(e.toString()));
//     }
//   };
// }

// export function CreatePurchaseRequestComponentStart(): CreatePurchaseRequestComponentStartAction {
//   return {
//     type: AuthActionType.CreatePurchaseRequestStart
//   };
// }

// export function CreatePurchaseRequestComponentSuccess(): CreatePurchaseRequestComponentSuccessAction {
//   toast.success(
//     <ToastComponent type={ToastType.Success} message={'Purchase Request created successfully.'} />,
//     ToastOptions.Success
//   );

//   return {
//     type: AuthActionType.CreatePurchaseRequestSuccess
//   };
// }

// export function CreatePurchaseRequestComponentFailure(e: string): CreatePurchaseRequestComponentFailureAction {
//   toast.error(<ToastComponent type={ToastType.Error} message={e} />, ToastOptions.Error);

//   return {
//     type: AuthActionType.CreatePurchaseRequestFailure,
//     errorMessage: e
//   };
// }

export function setAuthorizationReportId(id: number): SetReportIdAction {
  return {
    type: AuthActionType.SetReportId,
    id: id
  };
}

/***** Get Contract Cost History *****/

export function getContractCostHistory(contractId: number) {
  return async (dispatch: Dispatch<ContractTotalCostHistoryViewModel[]>) => {
    dispatch(getContractCostHistoryStart());
    try {
      let result = await contractService.getCostHistory(contractId);
      dispatch(getContractCostHistorySuccess(result));
    } catch (e) {
      dispatch(getContractCostHistoryFailure(e));
    }
  };
}

export function getContractCostHistoryStart(): GetContractCostHistoryStartAction {
  return {
    type: AuthActionType.GetContractCostHistoryStart
  };
}
export function getContractCostHistorySuccess(
  costHistory: Array<ContractTotalCostHistoryViewModel>
): GetContractCostHistorySuccessAction {
  return {
    type: AuthActionType.GetContractCostHistorySuccess,
    costHistory: costHistory
  };
}
export function getContractCostHistoryFailure(error: string): GetContractCostHistoryFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Failed to get Cost History: ' + error} />,
    ToastOptions.Error
  );

  return {
    type: AuthActionType.GetContractCostHistoryFailure,
    message: error
  };
}

export function getChargeBreakdown(swoId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    return dispatch(requestAction(() => authorizationService.getChargeBreakdown(swoId), setBreakdownAction));
  };
}

export function setBreakdownAction(breakdowns: Array<AuthorizationChargeBreakdown>): SetChargeBreakdownsAction {
  return {
    type: AuthActionType.SetChargeBreakdowns,
    breakdowns: breakdowns
  };
}

export function getAuthoHistory(swoId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getAuthoHistoryStart());
    try {
      const codeUpgrade = 63;
      const buildingPermit = 64;
      let codeUpgradeHistory = await authorizationService.getAuthoHistory(swoId, codeUpgrade);
      let buildingPermitHistory = await authorizationService.getAuthoHistory(swoId, buildingPermit);
      dispatch(getAuthoHistorySuccess(codeUpgradeHistory, buildingPermitHistory));
    } catch (e) {
      dispatch(getAuthoHistoryFailure(e));
    }
  };
}

export function getAuthoHistoryStart(): GetAuthoHistoryStartAction {
  return {
    type: AuthActionType.GetAuthoHistoryStart
  };
}
export function getAuthoHistorySuccess(
  codeUpgradeHistory: number,
  buildingPermitHistory: number
): GetAuthoHistorySuccessAction {
  return {
    type: AuthActionType.GetAuthoHistorySuccess,
    codeUpgradeHistory: codeUpgradeHistory,
    buildingPermitHistory: buildingPermitHistory
  };
}
export function getAuthoHistoryFailure(error: string): GetAuthoHistoryFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Failed to get Autho History: ' + error} />,
    ToastOptions.Error
  );

  return {
    type: AuthActionType.GetAuthoHistoryFailure,
    message: error
  };
}

export interface GetPurchaseRequestsAction extends Action {
  readonly type: AuthActionType.SetPurchaseRequests;
  readonly requests: PurchaseRequestSimpleViewModel[];
}
export function setPurchaseRequests(requests: PurchaseRequestSimpleViewModel[]): GetPurchaseRequestsAction {
  return {
    type: AuthActionType.SetPurchaseRequests,
    requests: requests
  };
}
export function getPurchaseRequests(swoId: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    return await dispatch(
      requestAction(() => swoService.getPurchaseRequests(swoId), setPurchaseRequests, {
        overrideFailMessage: 'Unable to get purchase requests.'
      })
    );
  };
}



// WEBPACK FOOTER //
// ./src/authorization/authorization.actions.tsx